/**
 * Export in the original format (0.99.0): no transcode. The whole file is copied byte for byte; a range of a video
 * or audio original is rewrapped by FFmpeg stream copy into the same container. Intra-only originals (and audio) are
 * cut exactly; long-GOP ones widen to the keyframe at or before In and the one at or after Out, never shorter.
 */
import { z } from 'zod';
import { CONTRACT_VERSION, LocationSpecsSchema, ObjectRefSchema, ProbeResultSchema, type ProbeResult } from './jobs.ts';
import { EXPORT_PRESETS, type ComposeResult, type ExportPreset } from './graphics.ts';
import { mimeForExtension } from './ffmpeg-profile.ts';

/** Video codecs where every frame is a keyframe, as ffprobe names them. */
const INTRA_CODECS = new Set([
  'prores', 'dnxhd', 'dnxhr', 'mjpeg', 'jpeg2000', 'dvvideo', 'v210', 'v410', 'r210', 'rawvideo', 'ffv1', 'cfhd', 'png', 'qtrle', 'huffyuv', 'utvideo',
]);

/**
 * A range of this original is cut frame-accurately: every video frame is a keyframe (ProRes, DNxHD/HR, AVC-Intra and
 * other H.264/HEVC Intra profiles, uncompressed…), or it has no picture at all (PCM and other audio).
 */
export function isIntraOnly(probe: Pick<ProbeResult, 'streams'> | null | undefined): boolean {
  const v = probe?.streams.find((s) => s.type === 'video');
  if (!v) return true;
  const codec = (v.codec ?? '').toLowerCase();
  if (INTRA_CODECS.has(codec)) return true;
  if ((codec === 'h264' || codec === 'hevc') && /intra/i.test(v.profile ?? '')) return true;
  return false;
}

/**
 * The range a long-GOP rewrap really cuts: from the last keyframe at or before In (the file start when there is
 * none) to the first keyframe at or after Out (the file end when there is none). Never shorter than asked.
 */
export function keyframeRange(keyframes: number[], inTicks: number, outTicks: number, durationTicks: number): { inTicks: number; outTicks: number } {
  let start = 0;
  let end = durationTicks;
  for (const k of keyframes) {
    if (k <= inTicks && k > start) start = k;
    if (k >= outTicks && k < end) end = k;
  }
  return { inTicks: Math.min(start, inTicks), outTicks: Math.max(end, Math.min(outTicks, durationTicks)) };
}

/** How a container is rewritten: FFmpeg's muxer, the file extension and a short label. */
export interface RewrapContainer { muxer: string; extension: string; label: string }

const CONTAINERS: Record<string, RewrapContainer> = {
  mov: { muxer: 'mov', extension: 'mov', label: 'MOV' },
  mp4: { muxer: 'mp4', extension: 'mp4', label: 'MP4' },
  m4v: { muxer: 'mp4', extension: 'm4v', label: 'M4V' },
  mxf: { muxer: 'mxf', extension: 'mxf', label: 'MXF' },
  mkv: { muxer: 'matroska', extension: 'mkv', label: 'MKV' },
  webm: { muxer: 'webm', extension: 'webm', label: 'WebM' },
  avi: { muxer: 'avi', extension: 'avi', label: 'AVI' },
  ts: { muxer: 'mpegts', extension: 'ts', label: 'MPEG-TS' },
  wav: { muxer: 'wav', extension: 'wav', label: 'WAV' },
  m4a: { muxer: 'ipod', extension: 'm4a', label: 'M4A' },
  mp3: { muxer: 'mp3', extension: 'mp3', label: 'MP3' },
  aif: { muxer: 'aiff', extension: 'aif', label: 'AIFF' },
  aiff: { muxer: 'aiff', extension: 'aiff', label: 'AIFF' },
  flac: { muxer: 'flac', extension: 'flac', label: 'FLAC' },
};

/** The original's extension, lower case ('' when it has none). */
export function originalExtension(filename: string | null | undefined): string {
  const m = /\.([A-Za-z0-9]{1,8})$/.exec(filename ?? '');
  return m ? m[1]!.toLowerCase() : '';
}

/**
 * How a range of this original is rewrapped into its own container; null when it cannot be (stills, graphics and
 * documents, and containers PRISM does not rewrap), which leaves only the whole-file copy.
 */
export function rewrapContainer(originalFilename: string | null | undefined, probe: Pick<ProbeResult, 'mediaKind' | 'durationTicks'> | null | undefined): RewrapContainer | null {
  if (!probe || (probe.mediaKind !== 'video' && probe.mediaKind !== 'audio') || !probe.durationTicks) return null;
  return CONTAINERS[originalExtension(originalFilename)] ?? null;
}

/**
 * What an export's file is: its preset's label and extension, or for the original format the extension it was
 * written with. Takes an export DTO (its `format`) or a row's preset and output extension.
 */
export function exportFormat(e: { preset: string; outputExt?: string | null; format?: { label: string; extension: string } | null }): { label: string; extension: string; mime: string } {
  if (e.format) return { ...e.format, mime: mimeForExtension(e.format.extension) };
  if (e.preset === 'original') {
    const extension = e.outputExt || 'bin';
    return { label: `Original format · ${extension.toUpperCase()}`, extension, mime: mimeForExtension(extension) };
  }
  const p = EXPORT_PRESETS[e.preset as ExportPreset];
  return p ? { label: p.label, extension: p.extension, mime: p.mime } : { label: e.preset, extension: e.outputExt || 'mp4', mime: mimeForExtension(e.outputExt || 'mp4') };
}

/** "MOV, ProRes 422 HQ": the original's container and main codec, for the Format list. */
export function originalFormatLabel(filename: string | null | undefined, probe: Pick<ProbeResult, 'streams'> | null | undefined): string {
  const ext = originalExtension(filename);
  const container = CONTAINERS[ext]?.label ?? (ext ? ext.toUpperCase() : 'file');
  const s = probe?.streams.find((x) => x.type === 'video') ?? probe?.streams.find((x) => x.type === 'audio');
  const codec = s?.codec ? `${CODEC_LABELS[s.codec] ?? s.codec}${s.profile && s.profile !== 'unknown' && !/^LC$/.test(s.profile) ? ` ${s.profile}` : ''}` : null;
  // A still's codec is its container (PNG, PNG): name it once.
  return codec && codec.toLowerCase() !== container.toLowerCase() ? `${container}, ${codec}` : container;
}
/** Codec names as shown (also in a master's format, masters.ts). */
export const CODEC_LABELS: Record<string, string> = {
  prores: 'ProRes', dnxhd: 'DNxHD/HR', h264: 'H.264', hevc: 'HEVC', mpeg2video: 'MPEG-2', mjpeg: 'Motion JPEG', jpeg2000: 'JPEG 2000', dvvideo: 'DV',
  aac: 'AAC', mp3: 'MP3', flac: 'FLAC', pcm_s16le: 'PCM 16-bit', pcm_s24le: 'PCM 24-bit', pcm_s32le: 'PCM 32-bit', pcm_s16be: 'PCM 16-bit', pcm_s24be: 'PCM 24-bit',
};

/** A rewrap of a range of an original into its own container, by stream copy (0.99.0). */
export const RewrapJobSchema = z.object({
  contractVersion: z.literal(CONTRACT_VERSION),
  correlationId: z.string(),
  exportId: z.string().uuid(),
  source: ObjectRefSchema,
  probe: ProbeResultSchema,
  container: z.object({ muxer: z.string(), extension: z.string(), label: z.string() }),
  /** The range asked for, in ticks from the start of the file. */
  inTicks: z.number().int().nonnegative(),
  outTicks: z.number().int().positive(),
  destination: ObjectRefSchema,
  locations: LocationSpecsSchema,
});
export type RewrapJob = z.infer<typeof RewrapJobSchema>;

export interface RewrapResult extends ComposeResult {
  /** The range in the file: wider than asked when a long-GOP cut moved to keyframes. */
  actualInTicks: number;
  actualOutTicks: number;
  exact: boolean;
}

/** What the workflow tells completeExport about an Original format export. */
export interface OriginalExportOutcome {
  mode: 'copy' | 'rewrap';
  actualInTicks?: number;
  actualOutTicks?: number;
  exact?: boolean;
}
